import { GatsbyImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import styled from 'styled-components';

import {
  Link,
  PriceRange,
  WysiwygStyleWrapper,
  Attribution,
  HtmlContentLoader,
  Slider,
  Icon,
} from '@powdr/components';
import {
  AlternatingBlockLayoutOptions,
  BlockTypes,
  CarouselControlPlacement,
  DorPlaceStatus,
  NavigationDirection,
  ThemeProps as Prop,
  StaticProfiles,
} from '@powdr/constants';
import { ContentBlockType } from '@powdr/model';
import {
  themeUtil, formatDate, phoneNumberPrettify,
  getDorStatusColor,
  capitalizeAllWords,
  deslugify,
} from '@powdr/utils';

const CPNT = BlockTypes.BASIC;

// styled components
const ContentBlockBasic = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  ${({ $verticalAlignment }) => ($verticalAlignment) && `justify-content: ${$verticalAlignment}`};

  .link-icon {
    margin-left: 5px;
    margin-top: -3px;
    height: 1em;
    width: 1em;

    svg {
      fill: ${({ $colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, $colorProfile)};
      color: ${({ $colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, $colorProfile)};
    }
  }

  .image-container {
    position: relative;
  }

  .video-icon {
    display: inline-block;
    position: absolute;
    top: calc(50% - 80px);
    left: calc(50% - 40px);

    svg {
      fill: ${({ $colorProfile }) => themeUtil(Prop.ICON, CPNT, $colorProfile)};
      color: ${({ $colorProfile }) => themeUtil(Prop.ICON, CPNT, $colorProfile)};
    }

    &:hover {
      fill: ${({ $colorProfile }) => themeUtil(Prop.ICON_HOVER, CPNT, $colorProfile)};
      color: ${({ $colorProfile }) => themeUtil(Prop.ICON_HOVER, CPNT, $colorProfile)};
    }
  }

  .cb-title {
    margin-bottom: 15px;
  }

  .cb-copy {
    margin-bottom: 15px;
  }

  .cb-basic-button {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 15px 0;
    color: ${({ $colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, $colorProfile)};
  }

  &:hover {
    .cb-basic-button {
      color: ${({ $colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, $colorProfile)};
    }

    .link-icon {
      svg {
        fill: ${({ $colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, $colorProfile)};
        color: ${({ $colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, $colorProfile)};
      }
    }
  }

  small:not(.template) {
    margin-bottom: 10px;
  }
`;

const TopContainer = styled.div`
  margin-bottom: 15px;
`;

const ImageContainer = styled(Link)`
  display: grid;
  margin-bottom: 15px;
`;

const StyledSlider = styled(Slider)`
  margin-bottom: 15px;
`;

const StyledImage = styled(GatsbyImage)`
  grid-area: 1/1;
`;

const Image = styled.img`
  grid-area: 1/1;
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TagContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 50px;
  margin-bottom: 10px;
`;

const AdditionalInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  line-height: 1.2;
  padding: ${({ $alternateBlockLayoutState }) => (
    ($alternateBlockLayoutState === AlternatingBlockLayoutOptions.DEFAULT)
      ? '30px 0'
      : '0'
  )};

  a {
    transition: color .25s ease-out;
    color: ${({ $colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, $colorProfile)};

    &:hover {
      color: ${({ $colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, $colorProfile)};
    }
  }
`;

const AILeft = styled.div`
  width: 66%;
`;

const CustomPriceRange = styled(PriceRange)`
  font-weight: bold;
`;

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: ${({ $alternateBlockLayoutState }) => (
    ($alternateBlockLayoutState === AlternatingBlockLayoutOptions.DEFAULT)
      ? 'column'
      : 'column-reverse'
  )};
`;

const MainContentContainer = styled.div``;

const AIRight = styled.div`
  display: flex;
  flex-direction: column;
  text-align: right;
`;

const AIRightTop = styled.div`
  display: flex;
  flex-direction: column;
  font-weight: bold;
`;

const AIRightBottom = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatusHeader = styled.small``;

const StatusValue = styled.small`
  font-weight: normal;
  color: ${({ $status }) => getDorStatusColor(
    DorPlaceStatus,
    $status,
    CPNT,
    StaticProfiles.ALERTS,
  )} !important;
`;

const Date = styled.p`
  text-align: left;
  line-height: 1.1;
  font-weight: bold;
`;

const Tags = styled.p`
  text-align: right;
  font-weight: bold;
  line-height: 1.1;
  text-transform: capitalize;
`;

export const Basic = ({
  title, copy, blockType, colorProfile, image, imageCarousel, videoId, isStaticImageLink, ariaLabel,
  buttonEnabled, buttonLink, buttonText, buttonTarget, buttonClick, date, endDate,
  altButtonEnabled, altButtonText, altButtonLink, altButtonTarget,
  formattedContent, tags, status, verticalAlignment, alternateBlockLayoutState,
  hours, phoneNumber, phoneNumberDetails, location, locationDetails, priceRange,
}) => (
  <ContentBlockBasic
    className={`content-block-${blockType}`}
    $colorProfile={colorProfile}
    $verticalAlignment={verticalAlignment}
  >
    <TopContainer>
      {(date || tags) && (
      <TagContainer role="none">
        {(date) && <Date role="none">{`${formatDate(date).month.num}/${formatDate(date).day.num}/${formatDate(date).year}${(endDate && date !== endDate) ? ` - ${formatDate(endDate).month.num}/${formatDate(endDate).day.num}/${formatDate(endDate).year}` : ''}`}</Date>}
        {(tags) && <Tags role="none">{tags}</Tags>}
      </TagContainer>
      )}
      {(imageCarousel || image) && (
        (imageCarousel) ? (
          <StyledSlider
            relationships={{ slides: imageCarousel }}
            parentColorProfile={colorProfile}
            isThumbnailsDisabled
            additionalControls={{
              controlSettings: {
                navigation: {
                  placement: CarouselControlPlacement.ON_SLIDE,
                  iconName: 'ui-arrow-left',
                  iconSize: 20,
                  classes: {
                    [NavigationDirection.PREVIOUS]: 'cb-control-previous',
                    [NavigationDirection.NEXT]: 'cb-control-next',
                  },
                },
              },
            }}
          />
        ) : (
          <ImageContainer
            onClick={() => buttonClick()}
            aria-label={ariaLabel}
            role="button"
            href={buttonLink}
            target={buttonTarget}
          >
            {(!isStaticImageLink) && (
            <StyledImage
              image={image.src}
              alt={image?.altText}
            />
            )}
            {(isStaticImageLink) && (
            <Image
              src={image.src}
              alt={image?.altText}
            />
            )}
            {(image?.attribution) && <Attribution>{image?.attribution}</Attribution>}
            {(videoId) && <Icon className="video-icon" name="content-play" width="100" height="100" />}
          </ImageContainer>
        )
      )}
      {(title) && <h3 className="cb-title">{title}</h3>}
      <LayoutContainer $alternateBlockLayoutState={alternateBlockLayoutState}>
        {(hours || phoneNumber || location || status) && (
        <AdditionalInfoContainer
          $colorProfile={colorProfile}
          $alternateBlockLayoutState={alternateBlockLayoutState}
        >
          <AILeft>
            {(priceRange?.length > 0) && <CustomPriceRange max={4} value={priceRange.length || 1} character="$" />}
            <div>{ReactHtmlParser(hours)}</div>
          </AILeft>
          <AIRight>
            {(status) && (
              <AIRightTop>
                <StatusHeader className="template">Status</StatusHeader>
                <StatusValue className="template" $status={status}>{capitalizeAllWords(deslugify(status))}</StatusValue>
              </AIRightTop>
            )}
            {(location || locationDetails) && (
              <AIRightTop>
                {(location) && <small className="template">{location}</small>}
                {(locationDetails) && <small className="template">{locationDetails}</small>}
              </AIRightTop>
            )}
            {(phoneNumber || phoneNumberDetails) && (
            <AIRightBottom>
              {(phoneNumber) && <small className="template"><a href={`tel:${phoneNumber}`}>{phoneNumberPrettify(phoneNumber)}</a></small>}
              {(phoneNumberDetails) && <small className="template">{phoneNumberDetails}</small>}
            </AIRightBottom>
            )}
          </AIRight>
        </AdditionalInfoContainer>
        )}
        <MainContentContainer>
          {(copy) && <p className="cb-copy">{ReactHtmlParser(copy)}</p>}
          {(formattedContent) && <WysiwygStyleWrapper className="cb-formatted-content"><HtmlContentLoader html={formattedContent} allowRerender /></WysiwygStyleWrapper>}
        </MainContentContainer>
      </LayoutContainer>
    </TopContainer>
    <ButtonWrapper>
      {(buttonText && buttonEnabled) && (
        <Link
          className="button with-background"
          onClick={() => buttonClick()}
          aria-label={ariaLabel}
          role="button"
          href={buttonLink}
          target={buttonTarget}
        >
          {buttonText}
        </Link>
      )}
      {(altButtonEnabled) && (
        <Link
          className="button with-background"
          onClick={() => buttonClick()}
          aria-label={ariaLabel}
          role="button"
          href={altButtonLink}
          target={altButtonTarget || buttonTarget}
        >
          {altButtonText}
        </Link>
      )}
    </ButtonWrapper>
  </ContentBlockBasic>
);

Basic.propTypes = PropTypes.objectOf(ContentBlockType).isRequired;
